import { ReactElement, useState, ChangeEvent, KeyboardEvent } from 'react'
import Dialog, { DialogTitle, DialogContent, DialogActions } from 'beam/dist/Dialog'
import { FormControl } from 'beam/dist/Form'
import Button, { LoadingButton } from 'beam/dist/Button'
import { TextField } from 'beam'
import PasswordStrengthIndicator, { PasswordStrength, StrengthT } from '../../Common/PasswordStrengthIndicator'
import { useTranslation } from 'react-i18next'

interface DialogProps {
  open: boolean,
  onClose: () => void,
  onUpdatePassword: (password: string) => void,
  passwordStrength: StrengthT,
  onConfirm: (currentPassword: string, newPassword: string) => void,
  error: string,
  loading: boolean,
}

export function PasswordDialog({ open, onClose, onUpdatePassword, passwordStrength, onConfirm, error, loading }: DialogProps): ReactElement {
  const { t } = useTranslation()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [mismatch, setMismatch] = useState(false) // shown when passwords don't match


  const updateCurrentPassword = (event: ChangeEvent<HTMLInputElement>): void => {
    setCurrentPassword(event.target.value)
  }

  const updateNewPassword = (event: ChangeEvent<HTMLInputElement>): void => {
    const password = event.target.value
    onUpdatePassword(password)
    setNewPassword(password)
  }

  const updateConfirmPassword = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    await setConfirmPassword(event.target.value)
    if (mismatch) {
      comparePasswords()
    }
  }

  const comparePasswords = (): void => {
    setMismatch(confirmPassword !== newPassword)
  }

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      handleConfirmClick()
    }
  }

  const disableConfirm = (): boolean | undefined => {

    return !currentPassword || !newPassword || confirmPassword !== newPassword
  }

  const handleConfirmClick = (): void => {
    if (!disableConfirm()) {
      onConfirm(currentPassword, newPassword)
    }
  }

  const handleClose = (): void => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setMismatch(false)

    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{t('changePassword')}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth onKeyPress={handleKeyPress}>
          <TextField
            label={error || t('currentPassword')}
            error={Boolean(error)}
            type="password"
            onChange={updateCurrentPassword}
            fullWidth
          />
          <TextField label={t('newPassword')} type="password" onChange={updateNewPassword} fullWidth />
          <PasswordStrengthIndicator strength={passwordStrength} />
          <TextField
            error={Boolean(newPassword && confirmPassword && mismatch)}
            label={t('confirmPassword')}
            type="password"
            onChange={updateConfirmPassword}
            onBlur={comparePasswords}
            fullWidth
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          disabled={disableConfirm()}
          onClick={handleConfirmClick}
          color="primary"
          loading={loading}
        >
          {t('changePassword')}
        </LoadingButton>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export { PasswordStrength }
export default PasswordDialog
